/**
 * Cycle Configuration Session Types
 * 
 * A CycleConfigurationSession is a distinct entity that manages cycle-wide
 * configuration decisions BEFORE prioritization sessions are run.
 * 
 * This enables:
 * - Collaborative configuration of criteria, weights, tools
 * - Control over who can provide values for which attributes
 * - Live or async configuration with facilitator or participant entry
 * - Locking of configuration to prevent changes during prioritization
 * 
 * The configuration session runs during the ENGAGE phase of the DEGAP cycle
 * and its locked decisions flow into all prioritization sessions in that cycle.
 */

import { v4 as uuidv4 } from 'uuid';
import type { ActorRole } from './roles';
import type { SessionTimingMode, SessionEntryMode } from './session';

// ============================================================================
// Configuration Session State
// ============================================================================

/**
 * States for a cycle configuration session
 * 
 * - draft: Session created, configuration not started
 * - in_progress: Configuration is actively happening
 * - locked: Configuration is locked, no more changes allowed
 * - superseded: A newer configuration session has replaced this one
 */
export type CycleConfigState = 
  | 'draft' 
  | 'in_progress' 
  | 'locked' 
  | 'superseded';

// ============================================================================
// Configuration Scope
// ============================================================================

/**
 * What aspects of the cycle this configuration session covers
 */
export interface CycleConfigScope {
  /** Configure which criteria to use for prioritization */
  configureCriteria: boolean;
  
  /** Configure weights for the selected criteria */
  configureWeights: boolean;
  
  /** Configure which prioritization tools to use */
  configureTools: boolean;
  
  /** Configure who can provide values for which attributes */
  configureValueProviders: boolean;
  
  /** Configure who participates in prioritization sessions */
  configureParticipants: boolean;
}

/**
 * Default configuration scope - everything enabled
 */
export const DEFAULT_CONFIG_SCOPE: CycleConfigScope = {
  configureCriteria: true,
  configureWeights: true,
  configureTools: true,
  configureValueProviders: true,
  configureParticipants: true,
};

// ============================================================================
// Configuration Decisions
// ============================================================================

/**
 * Types of configuration decisions that can be made
 */
export type CycleConfigDecisionType = 
  | 'criteria'          // Which criteria to use
  | 'weights'           // Weights for each criterion
  | 'tools'             // Which tools to use
  | 'value_providers'   // Who can provide values for which attributes
  | 'attribute_ranges'  // Value range definitions for each attribute (e.g., Fibonacci, T-shirt)
  | 'participants';     // Who participates in sessions

/**
 * Status of a configuration decision
 */
export type CycleConfigDecisionStatus = 
  | 'pending'    // Not yet configured
  | 'proposed'   // Value proposed but not locked
  | 'locked';    // Locked, no changes allowed

/**
 * A single entry in the decision history
 */
export interface DecisionHistoryEntry {
  /** When this change was made */
  timestamp: Date;
  
  /** Who made this change */
  userId: string;
  userName?: string;
  
  /** What action was taken */
  action: 'proposed' | 'revised' | 'accepted' | 'rejected' | 'locked' | 'unlocked';
  
  /** The value at this point in history */
  value: unknown;
  
  /** Optional rationale for the change */
  rationale?: string;
}

/**
 * A configuration decision within the session
 */
export interface CycleConfigDecision {
  /** Type of decision */
  type: CycleConfigDecisionType;
  
  /** Current status */
  status: CycleConfigDecisionStatus;
  
  /** The current value (type depends on decision type) */
  value: unknown;
  
  /** Who locked this decision */
  lockedBy?: string;
  
  /** When this decision was locked */
  lockedAt?: Date;
  
  /** History of changes to this decision */
  history: DecisionHistoryEntry[];
}

// ============================================================================
// Criteria Decision Value
// ============================================================================

/**
 * Value structure for criteria selection decision
 */
export interface CriteriaDecisionValue {
  /** IDs of selected criteria */
  selectedCriteriaIds: string[];
  
  /** Labels for display (denormalized for convenience) */
  criteriaLabels: Record<string, string>;
}

// ============================================================================
// Weights Decision Value
// ============================================================================

/**
 * Value structure for weights decision
 */
export interface WeightsDecisionValue {
  /** Weights keyed by criteria ID (should sum to 100) */
  weights: Record<string, number>;
  
  /** Whether weights were auto-calculated or manually set */
  method: 'equal' | 'manual' | 'pairwise';
}

// ============================================================================
// Tools Decision Value
// ============================================================================

/**
 * Value structure for tools selection decision
 */
export interface ToolsDecisionValue {
  /** Selected tool IDs */
  selectedToolIds: string[];
  
  /** Primary tool (if multiple selected) */
  primaryToolId?: string;
  
  /** Tool-specific configuration */
  toolConfigs: Record<string, unknown>;
}

// ============================================================================
// Value Provider Configuration
// ============================================================================

/**
 * Configuration for who can provide and accept values for a specific attribute
 */
export interface ValueProviderConfig {
  /** Attribute ID this config applies to */
  attributeId: string;
  
  /** Attribute label for display */
  attributeLabel: string;
  
  /** Roles that can propose values for this attribute */
  proposerRoles: ActorRole[];
  
  /** Specific org units that can propose (optional - empty means all) */
  proposerOrgUnits?: string[];
  
  /** Roles that can accept/approve proposed values */
  acceptorRoles: ActorRole[];
  
  /** Specific org units that can accept (optional) */
  acceptorOrgUnits?: string[];
  
  /** 
   * Whether the user must have a matching app-level role to propose values.
   * For example, only users with "engineer" app role can estimate engineering effort.
   */
  requiresAppRoleMatch?: boolean;
  
  /** 
   * If requiresAppRoleMatch is true, which app-level role is required?
   * Allows mapping cycle roles to specific app roles for validation.
   */
  requiredAppRoles?: ActorRole[];
  
  /** Whether this attribute requires a value before prioritization */
  isRequired: boolean;
  
  /** Default value if none provided */
  defaultValue?: unknown;
}

/**
 * Value structure for value providers decision
 */
export interface ValueProvidersDecisionValue {
  /** Configuration for each attribute */
  attributeConfigs: ValueProviderConfig[];
}

// ============================================================================
// Attribute Range Configuration
// ============================================================================

/**
 * Value range type for an attribute
 */
export type AttributeRangeType = 
  | 'select'      // Predefined options (e.g., Low/Medium/High)
  | 'number'      // Numeric range
  | 'scale'       // 1-N scale
  | 'fibonacci'   // Fibonacci sequence (1, 2, 3, 5, 8, 13, 21)
  | 'tshirt'      // T-shirt sizes (XS, S, M, L, XL, XXL)
  | 'custom';     // Custom options

/**
 * Configuration for an attribute's value range in a cycle
 */
export interface AttributeRangeConfig {
  /** Attribute key this config applies to */
  attributeKey: string;
  
  /** Attribute label for display */
  attributeLabel: string;
  
  /** Type of value range */
  rangeType: AttributeRangeType;
  
  /** Available options (for select, custom types) */
  options?: (string | number)[];
  
  /** Min value (for number, scale types) */
  minValue?: number;
  
  /** Max value (for number, scale types) */
  maxValue?: number;
  
  /** Step size (for number type) */
  step?: number;
  
  /** Labels for options (optional mapping) */
  optionLabels?: Record<string, string>;
  
  /** Status of this range configuration */
  status: 'pending' | 'proposed' | 'accepted';
  
  /** Who proposed this range */
  proposedBy?: string;
  proposedAt?: Date;
  
  /** Who accepted this range */
  acceptedBy?: string;
  acceptedAt?: Date;
}

/**
 * Value structure for attribute ranges decision
 */
export interface AttributeRangesDecisionValue {
  /** Range configuration for each attribute */
  attributeRanges: AttributeRangeConfig[];
  
  /** Whether all ranges are accepted */
  allRangesAccepted: boolean;
}

// ============================================================================
// Participants Configuration
// ============================================================================

/**
 * Participant role in the configuration session
 */
export type ConfigParticipantRole = 
  | 'owner'       // Can lock decisions, final authority
  | 'contributor' // Can propose and vote on decisions
  | 'observer';   // Can view but not modify

/**
 * A participant in the configuration session
 */
export interface CycleConfigParticipant {
  /** User ID */
  userId: string;
  
  /** Display name */
  userName?: string;
  
  /** Role in this configuration session */
  role: ConfigParticipantRole;
  
  /** When they were added */
  addedAt: Date;
  
  /** Who added them */
  addedBy: string;
  
  /** Their app-level roles (for validation) */
  appRoles?: ActorRole[];
  
  /** Whether they have participated yet */
  hasParticipated: boolean;
  
  /** Last participation time */
  lastParticipatedAt?: Date;
}

/**
 * Value structure for participants decision
 */
export interface ParticipantsDecisionValue {
  /** Participants for prioritization sessions */
  sessionParticipants: {
    userId: string;
    userName?: string;
    sessionRole: 'facilitator' | 'participant' | 'observer';
    appRoles?: ActorRole[];
  }[];
  
  /** Minimum required participants for a session to run */
  minimumParticipants?: number;
  
  /** Whether all participants must provide input */
  requireAllInput?: boolean;
}

// ============================================================================
// Main Configuration Session Entity
// ============================================================================

/**
 * A Cycle Configuration Session manages pre-session decisions for a DEGAP cycle.
 * 
 * This is a distinct entity from PrioritizationSession:
 * - ConfigurationSession: Decides HOW prioritization will work
 * - PrioritizationSession: Executes the prioritization with those settings
 */
export interface CycleConfigurationSession {
  /** Unique identifier */
  id: string;
  
  /** Parent DEGAP cycle ID */
  cycleId: string;
  
  /** Display name for this configuration session */
  name: string;
  
  /** Description/purpose */
  description?: string;
  
  // ================================
  // Session Type (DEGAP Alignment)
  // ================================
  
  /**
   * Session type - always 'engage' for CycleConfigurationSession (Setup Session)
   */
  sessionType: 'engage';
  
  // ================================
  // Mode Configuration
  // ================================
  
  /** Timing mode: live (synchronous) or async */
  timingMode: SessionTimingMode;
  
  /** Entry mode: facilitator enters for all, or participants enter their own */
  entryMode: SessionEntryMode;
  
  /** For async mode: when configuration opens */
  opensAt?: Date;
  
  /** For async mode: when configuration closes */
  closesAt?: Date;
  
  /** For live mode: scheduled start time */
  scheduledAt?: Date;
  
  // ================================
  // Scope & Participants
  // ================================
  
  /** What this session configures */
  configurationScope: CycleConfigScope;
  
  /** Participants in this configuration session */
  participants: CycleConfigParticipant[];
  
  // ================================
  // Decisions
  // ================================
  
  /** Configuration decisions */
  decisions: CycleConfigDecision[];
  
  // ================================
  // State
  // ================================
  
  /** Current state of the session */
  state: CycleConfigState;
  
  /** Version number (for optimistic concurrency) */
  version: number;
  
  // ================================
  // Timestamps
  // ================================
  
  /** When the session was created */
  createdAt: Date;
  
  /** Who created the session */
  createdBy: string;
  
  /** When the session was last updated */
  updatedAt: Date;
  
  /** When the session was locked */
  lockedAt?: Date;
  
  /** Who locked the session */
  lockedBy?: string;
  
  /** Reason for locking */
  lockReason?: string;
}

// ============================================================================
// Factory Functions
// ============================================================================

/**
 * Create a new empty configuration session
 */
export function createEmptyConfigSession(
  cycleId: string,
  name: string,
  createdBy: string,
  scope: Partial<CycleConfigScope> = {}
): CycleConfigurationSession {
  const now = new Date();
  const fullScope: CycleConfigScope = { ...DEFAULT_CONFIG_SCOPE, ...scope };
  
  const decisions: CycleConfigDecision[] = [];
  
  if (fullScope.configureCriteria) {
    decisions.push({
      type: 'criteria',
      status: 'pending',
      value: { selectedCriteriaIds: [], criteriaLabels: {} } as CriteriaDecisionValue,
      history: [],
    });
  }
  
  if (fullScope.configureWeights) {
    decisions.push({
      type: 'weights',
      status: 'pending',
      value: { weights: {}, method: 'equal' } as WeightsDecisionValue,
      history: [],
    });
  }
  
  if (fullScope.configureTools) {
    decisions.push({
      type: 'tools',
      status: 'pending',
      value: { selectedToolIds: [], toolConfigs: {} } as ToolsDecisionValue,
      history: [],
    });
  }
  
  if (fullScope.configureValueProviders) {
    decisions.push({
      type: 'value_providers',
      status: 'pending',
      value: { attributeConfigs: [] } as ValueProvidersDecisionValue,
      history: [],
    });
  }
  
  if (fullScope.configureParticipants) {
    decisions.push({
      type: 'participants',
      status: 'pending',
      value: { sessionParticipants: [] } as ParticipantsDecisionValue,
      history: [],
    });
  }
  
  return {
    // cycle_config_sessions.id is a uuid column: a `config-<time>-<random>` id was refused on every insert and update (22P02).
    id: uuidv4(),
    cycleId,
    name,
    timingMode: 'live',
    entryMode: 'facilitator',
    configurationScope: fullScope,
    participants: [{
      userId: createdBy,
      role: 'owner',
      addedAt: now,
      addedBy: createdBy,
      hasParticipated: false,
    }],
    decisions,
    state: 'draft',
    version: 1,
    createdAt: now,
    createdBy,
    updatedAt: now,
  };
}

/**
 * Get a decision by type from a configuration session
 */
export function getDecision(
  session: CycleConfigurationSession,
  type: CycleConfigDecisionType
): CycleConfigDecision | undefined {
  return session.decisions.find(d => d.type === type);
}

/**
 * Check if all required decisions are locked
 */
export function areAllDecisionsLocked(session: CycleConfigurationSession): boolean {
  return session.decisions.every(d => d.status === 'locked');
}

/**
 * Check if a session can be locked
 */
export function canLockSession(session: CycleConfigurationSession): {
  canLock: boolean;
  reasons: string[];
} {
  const reasons: string[] = [];
  
  if (session.state === 'locked') {
    reasons.push('Session is already locked');
  }
  
  if (session.state === 'superseded') {
    reasons.push('Session has been superseded');
  }
  
  // Check that all decisions have values
  for (const decision of session.decisions) {
    if (decision.status === 'pending') {
      reasons.push(`${decision.type} has not been configured`);
    }
  }
  
  return {
    canLock: reasons.length === 0,
    reasons,
  };
}

/**
 * Get the locked configuration for use in sessions
 */
export function getLockedConfiguration(
  session: CycleConfigurationSession
): {
  criteria?: CriteriaDecisionValue;
  weights?: WeightsDecisionValue;
  tools?: ToolsDecisionValue;
  valueProviders?: ValueProvidersDecisionValue;
  participants?: ParticipantsDecisionValue;
} | null {
  if (session.state !== 'locked') {
    return null;
  }
  
  const result: ReturnType<typeof getLockedConfiguration> = {};
  
  for (const decision of session.decisions) {
    if (decision.status === 'locked') {
      switch (decision.type) {
        case 'criteria':
          result.criteria = decision.value as CriteriaDecisionValue;
          break;
        case 'weights':
          result.weights = decision.value as WeightsDecisionValue;
          break;
        case 'tools':
          result.tools = decision.value as ToolsDecisionValue;
          break;
        case 'value_providers':
          result.valueProviders = decision.value as ValueProvidersDecisionValue;
          break;
        case 'participants':
          result.participants = decision.value as ParticipantsDecisionValue;
          break;
      }
    }
  }
  
  return result;
}

export default {
  createEmptyConfigSession,
  getDecision,
  areAllDecisionsLocked,
  canLockSession,
  getLockedConfiguration,
  DEFAULT_CONFIG_SCOPE,
};
